import { useEffect, useState } from 'react';
import { Box, Divider, Heading } from '@chakra-ui/react';
import { Button, Icon, Text } from '@mercator-gcp/grout';
import axios, { AxiosError } from 'axios';
import { startCase, toLower } from 'lodash';
import { EmptyField } from 'components/elements/EmptyField';
import { Spinner } from 'components/elements/Spinner';
import { LogoIcon } from 'components/icons/LogoIcon';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Panel } from 'components/layout/Panel';
import PageMetadata from 'components/PageMetadata';
import MarketNotInSubscription from 'components/widgets/Error/MarketNotInSubscription';
import PageNotFound from 'components/widgets/Error/PageNotFound';
import SomethingWentWrong from 'components/widgets/Error/SomethingWentWrong';
import Notes from 'components/widgets/Notes';
import CreatedAtDateDisplay from 'components/widgets/ProjectDetails/CreatedAtDateDisplay';
import ProjectInfo from 'components/widgets/ProjectDetails/ProjectInfo';
import ProjectDetailsWithAISection from 'components/widgets/ProjectDetails/sections/ProjectDetailsWithAISection';
import { useProjectDetails } from 'hooks/useProjectDetails';
import { useProjectNotes } from 'hooks/useProjectNotes';
import { ActivityList } from 'pages/ProjectDetails/ActivityList';
import InvolvedCompanies from 'pages/ProjectDetails/InvolvedCompanies';
import PastProjectsSection from 'pages/ProjectDetails/PastProjectsSection';
import SummaryView from 'pages/ProjectDetails/SummaryView';
import { useAddressQuery } from 'state/queryParams';

const dividerStyle = {
  borderColor: '#CCD1CC',
  borderWidth: '1px',
};

const topNav = {
  row: {
    justifyContent: 'center',
    paddingBottom: (theme: any) => theme.space.sixteen,
    paddingTop: (theme: any) => theme.space.sixteen,
    background: 'brand.white',
  },
  box1: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    position: 'relative',
  },
  headingContainer: {
    display: 'inline-flex',
    gap: 8,
  },
  heading: {
    color: 'brand.army-dark',
    fontSize: '20px',
  },
  text: {
    color: 'brand.text.quiet',
    fontSize: '13px',
  },
};

const pageContainerStyle = {
  overflowY: 'auto',
  height: '100vh',
  '@media print': {
    overflow: 'visible',
    height: 'auto',
  },
};

const printContainerStyle = {
  width: '216mm',
  margin: 'auto',
  marginTop: (theme: any) => theme.space.twentyFour,
  marginBottom: (theme: any) => theme.space.twentyFour,
};

const pdpSectionStyle = {
  marginTop: (theme: any) => theme.space.thirtyTwo,
};

const footerSection = {
  ...pdpSectionStyle,
  alignItems: 'flex-start',
  columnGap: (theme: any) => theme.space.sixteen,
};

const activitySectionsStyle = {
  marginTop: 'padding_16',
};

const activitySectionLabelStyle = {
  marginBottom: 'padding_12',
};

const activitySummaryStyle = {
  justifyContent: 'flex-start',
  alignItems: 'baseline',
  columnGap: 'padding_16',
  marginBottom: 'padding_12',
};

const loadingContainer = {
  height: '100vh',
};

const PrintPDP = ({ projectSlug }: { projectSlug: string }) => {
  // Use the custom hook for project details
  const {
    project,
    isLoadingProject,
    projectError,
    projectActivitySummary,
    isLoadingProjectActivitySummary,
    isErrorLoadingActivitySummary,
    involvedCompanies,
    isCompaniesInvolvedLoading,
    projectEvents,
    projectSizeInfo,
    projectWorkType,
    isProjectsByAddressLoading,
    projectsList,
    numberOfProjectsByAddress,
    setPage,
  } = useProjectDetails(projectSlug || '');

  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const { getProjectNote } = useProjectNotes();
  const { data, isLoading: notesLoading } = getProjectNote(Number(project?.id));
  const [, setAddress] = useAddressQuery();

  const openPrintDialog = () => {
    window.print();
  };

  useEffect(() => {
    const isLoading =
      isLoadingProject || isLoadingProjectActivitySummary || isProjectsByAddressLoading;
    if (!isLoading && isMapLoaded) {
      openPrintDialog();
    }
  }, [
    isLoadingProject,
    isLoadingProjectActivitySummary,
    isMapLoaded,
    isProjectsByAddressLoading,
  ]);

  useEffect(() => {
    setAddress(project?.address || '');

    return () => {
      setAddress('');
    };
  }, [project?.address, setAddress]);

  const combinedError =
    projectError || (project && axios.isAxiosError(project) ? project : null);

  if (!isLoadingProject && combinedError) {
    const axiosError = combinedError as AxiosError;
    if (axiosError?.response?.status === 403) {
      return <MarketNotInSubscription />;
    }
    if (axiosError?.response?.status === 404) {
      return <PageNotFound />;
    }

    return <SomethingWentWrong />;
  }

  if (
    isLoadingProject ||
    isLoadingProjectActivitySummary ||
    isCompaniesInvolvedLoading ||
    !project ||
    !projectActivitySummary ||
    notesLoading ||
    isProjectsByAddressLoading
  ) {
    return (
      <FlexRow sx={loadingContainer}>
        <Spinner />
      </FlexRow>
    );
  }

  return (
    <Box sx={pageContainerStyle}>
      <PageMetadata title={`${project?.address}`} />
      <Box sx={printContainerStyle}>
        <FlexRow>
          <FlexRow.Left>
            <LogoIcon height="60" width="180" />
          </FlexRow.Left>
          <FlexRow.Right>
            <Button
              variant="primary"
              className="hide-on-print"
              onClick={openPrintDialog}
              startAdornment={<Icon name={Icon.Name.Download} />}
            >
              Download PDF
            </Button>
          </FlexRow.Right>
        </FlexRow>
        <FlexRow sx={topNav.row}>
          <Box sx={topNav.box1}>
            <FlexRow sx={topNav.headingContainer}>
              <Heading size="sm" sx={topNav.heading}>
                {startCase(toLower(project?.address)) || <EmptyField />}
              </Heading>
              <Text sx={topNav.text}>{project?.city}</Text>
            </FlexRow>
          </Box>
        </FlexRow>

        <FlexRow>
          <Divider sx={dividerStyle} />
        </FlexRow>
        <FlexRow sx={pdpSectionStyle}>
          <ProjectInfo
            identifier={projectSlug || ''}
            onMapLoaded={() => setIsMapLoaded(true)}
            project={project}
            isLoading={isLoadingProject}
            projectSizeInfo={projectSizeInfo}
            projectWorkType={projectWorkType}
          />
        </FlexRow>
        <FlexRow sx={pdpSectionStyle}>
          {data?.data.note && !notesLoading && project?.id != null && (
            <Notes targetId={project.id} readOnly />
          )}
        </FlexRow>
        {project?.ai_insights && (
          <FlexRow sx={pdpSectionStyle}>
            <ProjectDetailsWithAISection
              projectSlug={projectSlug || ''}
              existingAiInsights={project.ai_insights}
              disableOnDemand
            />
          </FlexRow>
        )}
        <Box sx={pdpSectionStyle}>
          <Panel title="Project Stages" icon={<Icon name={Icon.Name.ArrowTrendUp} />}>
            <FlexColumn sx={activitySectionsStyle}>
              <FlexRow sx={activitySummaryStyle}>
                <Text.Lead>Summary</Text.Lead>
                <CreatedAtDateDisplay
                  createdAtDate={projectActivitySummary?.start_date}
                />
              </FlexRow>
              <SummaryView
                projectActivitySummary={projectActivitySummary}
                isLoading={isLoadingProjectActivitySummary}
                projectSlug={projectSlug || ''}
                showCompanyContacts
                isError={isErrorLoadingActivitySummary}
              />
            </FlexColumn>
            <FlexColumn sx={activitySectionsStyle}>
              <Text.Lead sx={activitySectionLabelStyle}>Full timeline</Text.Lead>
              <ActivityList
                projectId={projectSlug}
                activities={projectEvents || []}
                city={project?.city || ''}
              />
            </FlexColumn>
          </Panel>
        </Box>
        {project?.address && (
          <FlexColumn sx={activitySectionsStyle}>
            <Text.Lead sx={activitySectionLabelStyle}>Companies Involved</Text.Lead>
            <InvolvedCompanies involved={involvedCompanies || null} />
          </FlexColumn>
        )}
        <FlexRow sx={footerSection}>
          <PastProjectsSection
            projectSlug={projectSlug || ''}
            projectAddress={project?.address || ''}
            isLoading={isProjectsByAddressLoading}
            projectsList={projectsList}
            count={numberOfProjectsByAddress || 0}
            setPage={setPage}
          />
        </FlexRow>
      </Box>
    </Box>
  );
};

export default PrintPDP;
